import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function CreateHangman({ user, isEditMode = false }) {
  const navigate = useNavigate();
  const { hangmanId } = useParams();
  const [activity, setActivity] = useState({
    title: '',
    description: '',
    words: [],
    lives: 6,
    time_per_word: 60,
    points_per_word: 100,
    category_id: null,
    is_public: true,
    assigned_courses: []
  });
  
  const [currentWord, setCurrentWord] = useState({
    word: '',
    hint: '',
    category: ''
  });

  const [editingWordIndex, setEditingWordIndex] = useState(null); // Para rastrear qué palabra se está editando
  const [showCourseSelector, setShowCourseSelector] = useState(false);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(isEditMode);

  useEffect(() => {
    if (isEditMode && hangmanId) {
      loadExistingActivity();
    }
    fetchCourses();
    fetchCategories();
  }, [isEditMode, hangmanId]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/categories/${user.id}`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadExistingActivity = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/hangman/${hangmanId}`);
      setActivity(response.data);
    } catch (error) {
      console.error('Error loading activity:', error);
      alert('Error al cargar el ahorcado');
      navigate('/dashboard');
    } finally {
      setLoadingActivity(false);
    }
  };

  const fetchCourses = async () => {
    setLoadingCourses(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/classroom/courses/${user.id}`);
      setAvailableCourses(response.data.courses || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setLoadingCourses(false);
    }
  };

  const handleAddWord = () => {
    if (!currentWord.word.trim() || !currentWord.hint.trim()) {
      alert('Por favor completa la palabra y la pista');
      return;
    }

    if (editingWordIndex !== null) {
      // Modo edición: actualizar palabra existente
      const updatedWords = [...activity.words];
      updatedWords[editingWordIndex] = { ...currentWord };
      setActivity({
        ...activity,
        words: updatedWords
      });
      setEditingWordIndex(null);
    } else {
      // Modo agregar: añadir nueva palabra
      setActivity({
        ...activity,
        words: [...activity.words, { ...currentWord }]
      });
    }

    setCurrentWord({ word: '', hint: '', category: '' });
  };

  const handleEditWord = (index) => {
    setCurrentWord({ ...activity.words[index] });
    setEditingWordIndex(index);
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setCurrentWord({ word: '', hint: '', category: '' });
    setEditingWordIndex(null);
  };

  const handleRemoveWord = (index) => {
    // Si estamos editando la palabra que se va a eliminar, cancelar edición
    if (editingWordIndex === index) {
      handleCancelEdit();
    }
    setActivity({
      ...activity,
      words: activity.words.filter((_, i) => i !== index)
    });
  };

  const handleMoveWord = (index, direction) => {
    const newWords = [...activity.words];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    // Verificar límites
    if (targetIndex < 0 || targetIndex >= newWords.length) {
      return;
    }
    
    // Intercambiar posiciones
    [newWords[index], newWords[targetIndex]] = [newWords[targetIndex], newWords[index]];
    
    setActivity({
      ...activity,
      words: newWords
    });
    
    // Si estamos editando, actualizar el índice de edición
    if (editingWordIndex === index) {
      setEditingWordIndex(targetIndex);
    } else if (editingWordIndex === targetIndex) {
      setEditingWordIndex(index);
    }
  };

  const handleSave = async () => {
    if (!activity.title.trim() || activity.words.length === 0) {
      alert('Por favor agrega un título y al menos una palabra');
      return;
    }

    try {
      if (isEditMode && hangmanId) {
        await axios.put(`${BACKEND_URL}/api/hangman/${hangmanId}`, {
          ...activity,
          id: hangmanId,
          teacher_id: activity.teacher_id || user.id,
          teacher_name: activity.teacher_name || user.name  // Preserve or add teacher name
        });
        alert('Ahorcado actualizado exitosamente!');
      } else {
        await axios.post(`${BACKEND_URL}/api/hangman`, {
          ...activity,
          teacher_id: user.id,
          teacher_name: user.name  // Add teacher name
        });
        alert('Ahorcado creado exitosamente!');
      }
      navigate('/dashboard');
    } catch (error) {
      console.error('Error saving hangman:', error);
      alert(isEditMode ? 'Error al actualizar el ahorcado' : 'Error al crear el ahorcado');
    }
  };

  if (loadingActivity) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-gray-600">Cargando ahorcado...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-primary hover:text-purple-700 mb-2 flex items-center gap-2"
          >
            ← Volver al Dashboard
          </button>
          <h1 className="text-2xl font-bold text-gray-800">
            {isEditMode ? 'Editar Ahorcado' : 'Crear Ahorcado'} 🎯
          </h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Basic Info */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Información de la Actividad</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Título *</label>
              <input
                type="text"
                value={activity.title}
                onChange={(e) => setActivity({ ...activity, title: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Ej: Capitales Europeas"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Descripción</label>
              <textarea
                value={activity.description}
                onChange={(e) => setActivity({ ...activity, description: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Describe la actividad..."
                rows="3"
              />
            </div>

            {/* Category Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                🗂️ Categoría (opcional)
              </label>
              <select
                value={activity.category_id || ''}
                onChange={(e) => setActivity({ ...activity, category_id: e.target.value || null })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
              >
                <option value="">Sin categoría</option>
                {categories.filter(cat => !cat.parent_id).map(category => (
                  <optgroup key={category.id} label={`${category.icon} ${category.name}`}>
                    <option value={category.id}>
                      {category.icon} {category.name}
                    </option>
                    {categories.filter(sub => sub.parent_id === category.id).map(subcat => (
                      <option key={subcat.id} value={subcat.id}>
                        &nbsp;&nbsp;{subcat.icon} {subcat.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            {/* Configuration */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Vidas</label>
                <select
                  value={activity.lives}
                  onChange={(e) => setActivity({ ...activity, lives: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                >
                  <option value={1}>1 vida (Extremo)</option>
                  <option value={2}>2 vidas (Difícil)</option>
                  <option value={3}>3 vidas (Medio)</option>
                  <option value={4}>4 vidas</option>
                  <option value={6}>6 vidas (Normal)</option>
                  <option value={8}>8 vidas (Fácil)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tiempo/palabra (seg)</label>
                <input
                  type="number"
                  value={activity.time_per_word}
                  onChange={(e) => setActivity({ ...activity, time_per_word: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                  min={30}
                  max={300}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Puntos/palabra</label>
                <input
                  type="number"
                  value={activity.points_per_word}
                  onChange={(e) => setActivity({ ...activity, points_per_word: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                  min={10}
                  max={500}
                />
              </div>
            </div>

            {/* Assignment Section */}
            <div className="border-t pt-4 mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-3">Asignación</label>
              
              <div className="flex items-center mb-3">
                <input
                  type="checkbox"
                  id="is_public"
                  checked={activity.is_public}
                  onChange={(e) => {
                    setActivity({ 
                      ...activity, 
                      is_public: e.target.checked,
                      assigned_courses: e.target.checked ? [] : activity.assigned_courses
                    });
                  }}
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                />
                <label htmlFor="is_public" className="ml-2 text-sm text-gray-700">
                  <strong>Actividad Pública</strong> - Visible para todos
                </label>
              </div>

              {!activity.is_public && (
                <div>
                  <button
                    type="button"
                    onClick={() => setShowCourseSelector(!showCourseSelector)}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition flex items-center gap-2"
                  >
                    {activity.assigned_courses.length > 0 
                      ? `${activity.assigned_courses.length} clase(s) asignada(s)` 
                      : 'Asignar a clases'}
                  </button>

                  {showCourseSelector && (
                    <div className="mt-3 p-4 border border-gray-200 rounded-lg bg-gray-50">
                      {loadingCourses ? (
                        <p className="text-sm text-gray-600">Cargando clases...</p>
                      ) : availableCourses.length === 0 ? (
                        <p className="text-sm text-gray-600">No hay clases disponibles.</p>
                      ) : (
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {availableCourses.map((course) => (
                            <div key={course.id} className="flex items-center">
                              <input
                                type="checkbox"
                                id={`course_${course.id}`}
                                checked={activity.assigned_courses.includes(course.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setActivity({
                                      ...activity,
                                      assigned_courses: [...activity.assigned_courses, course.id]
                                    });
                                  } else {
                                    setActivity({
                                      ...activity,
                                      assigned_courses: activity.assigned_courses.filter(id => id !== course.id)
                                    });
                                  }
                                }}
                                className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                              />
                              <label htmlFor={`course_${course.id}`} className="ml-2 text-sm text-gray-700">
                                {course.name}
                              </label>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Add/Edit Word Form */}
        <div className={`bg-white rounded-xl shadow-sm p-6 mb-6 ${editingWordIndex !== null ? 'border-2 border-yellow-500' : ''}`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-800">
              {editingWordIndex !== null ? '✏️ Editar Palabra' : 'Agregar Palabra'}
            </h2>
            {editingWordIndex !== null && (
              <button
                onClick={handleCancelEdit}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition text-sm"
              >
                ✕ Cancelar Edición
              </button>
            )}
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Palabra * (case-sensitive)</label>
                <input
                  type="text"
                  value={currentWord.word}
                  onChange={(e) => setCurrentWord({ ...currentWord, word: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary font-mono"
                  placeholder="ls -la"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Categoría (opcional)</label>
                <input
                  type="text"
                  value={currentWord.category}
                  onChange={(e) => setCurrentWord({ ...currentWord, category: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                  placeholder="Comandos Linux"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Pista *</label>
              <input
                type="text"
                value={currentWord.hint}
                onChange={(e) => setCurrentWord({ ...currentWord, hint: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                placeholder="Comando para listar archivos con detalles"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleAddWord}
                className={`flex-1 px-6 py-3 text-white rounded-lg font-semibold transition flex items-center justify-center gap-2 ${
                  editingWordIndex !== null 
                    ? 'bg-yellow-500 hover:bg-yellow-600' 
                    : 'bg-success hover:bg-green-600'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {editingWordIndex !== null ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  )}
                </svg>
                {editingWordIndex !== null ? 'Actualizar Palabra' : 'Agregar Palabra'}
              </button>
            </div>
          </div>
        </div>

        {/* Words List */}
        {activity.words.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              Palabras Agregadas ({activity.words.length})
            </h2>
            <div className="space-y-3">
              {activity.words.map((word, index) => (
                <div 
                  key={index} 
                  className={`flex items-center justify-between p-4 border-2 rounded-lg transition ${
                    editingWordIndex === index 
                      ? 'border-yellow-500 bg-yellow-50' 
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-sm text-gray-500 font-mono">#{index + 1}</span>
                      <span className="font-bold text-lg text-primary font-mono">{word.word}</span>
                      {word.category && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                          {word.category}
                        </span>
                      )}
                      {editingWordIndex === index && (
                        <span className="px-2 py-1 bg-yellow-500 text-white text-xs font-semibold rounded">
                          ✏️ Editando
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">💡 {word.hint}</p>
                  </div>
                  <div className="flex gap-2">
                    {/* Reorder buttons */}
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => handleMoveWord(index, 'up')}
                        disabled={index === 0}
                        className="px-2 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition text-xs disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Mover arriba"
                      >
                        ↑
                      </button>
                      <button
                        onClick={() => handleMoveWord(index, 'down')}
                        disabled={index === activity.words.length - 1}
                        className="px-2 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition text-xs disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Mover abajo"
                      >
                        ↓
                      </button>
                    </div>
                    
                    {/* Edit and Delete buttons */}
                    <button
                      onClick={() => handleEditWord(index)}
                      disabled={editingWordIndex !== null && editingWordIndex !== index}
                      className={`px-3 py-2 rounded-lg font-semibold transition ${
                        editingWordIndex === index
                          ? 'bg-yellow-500 text-white'
                          : 'bg-blue-500 text-white hover:bg-blue-600'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleRemoveWord(index)}
                      className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="flex gap-4">
          <button
            onClick={handleSave}
            className="flex-1 px-6 py-4 bg-primary text-white rounded-xl font-bold text-lg hover:bg-purple-700 transition shadow-lg"
          >
            💾 {isEditMode ? 'Actualizar Ahorcado' : 'Guardar Ahorcado'}
          </button>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-4 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

export default CreateHangman;
